import { vec2 } from './geometry.js';
import { cellIndex, isWalkable, walkableNeighbours } from './grid.js';
/**
 * The squared gap between a cell centre and a blocked cell `span` cells away
 * along one axis.
 *
 * Cells are unit squares, so half a cell of the span is the mover's own cell
 * and half is the blocking cell: a rock in the very next cell leaves 0.5, one
 * further leaves 1.5. A span of zero is the cell itself and leaves nothing.
 * Squared, because the two axes are combined by Pythagoras and one square root
 * at the end is cheaper than two.
 */
const axisGap = (span) => {
    const gap = span - 0.5;
    return gap <= 0 ? 0 : gap * gap;
};
/**
 * For every cell, how many cells away the nearest rock in its own column is,
 * counting the rows off the top and bottom of the map as rock.
 *
 * Two linear scans per column. This is the first half of an exact distance
 * transform: with the vertical answer precomputed, the horizontal pass only has
 * to sweep columns, and the whole field costs a small multiple of the map
 * rather than one search per cell.
 */
const verticalSpans = (grid) => {
    const spans = new Array(grid.width * grid.height).fill(0);
    for (let x = 0; x < grid.width; x += 1) {
        // Each scan starts one cell beyond the edge, which is the off-map rock.
        let rock = -1;
        for (let y = 0; y < grid.height; y += 1) {
            if (!isWalkable(grid, vec2(x, y)))
                rock = y;
            spans[y * grid.width + x] = y - rock;
        }
        rock = grid.height;
        for (let y = grid.height - 1; y >= 0; y -= 1) {
            if (!isWalkable(grid, vec2(x, y)))
                rock = y;
            const index = y * grid.width + x;
            // `noUncheckedIndexedAccess` types the read as optional; the array was
            // filled to length above, so the fallback is unreachable.
            spans[index] = Math.min(spans[index] ?? 0, rock - y);
        }
    }
    return spans;
};
/**
 * The clearance of every cell of a map: the radius of the largest disc that
 * fits centred on it.
 *
 * Exact, not an approximation. A disc centred on a cell touches a rock at
 * `√(gapX² + gapY²)` where each gap is the distance from the cell centre to
 * that rock's nearest face, so the answer is the smallest such distance over
 * every rock — and, because the gap along an axis grows with the span, the only
 * rock worth testing in a column is the nearest one, which `verticalSpans`
 * already found. The horizontal sweep then walks outwards from the cell and
 * stops as soon as the horizontal gap alone exceeds the best answer so far, so
 * a cell hemmed in by rock — the common case in a cave — is measured by looking
 * at a handful of columns rather than at the map.
 *
 * Deterministic by construction: plain arithmetic in a fixed order, no
 * randomness and no clock, so the server and every client compute the identical
 * field for the identical map.
 */
export const createClearanceField = (grid) => {
    const spans = verticalSpans(grid);
    const clearances = new Array(grid.width * grid.height).fill(0);
    /** The vertical part of the answer for one column, treating columns off the map as solid rock. */
    const columnGap = (column, y) => column < 0 || column >= grid.width ? 0 : axisGap(spans[y * grid.width + column] ?? 0);
    for (let y = 0; y < grid.height; y += 1) {
        for (let x = 0; x < grid.width; x += 1) {
            const index = y * grid.width + x;
            if (grid.cells[index] !== true)
                continue;
            let best = Number.POSITIVE_INFINITY;
            // Terminates because the mover's own column is bounded by the top and
            // bottom of the map, which makes `best` finite on the very first pass,
            // and the horizontal gap grows without bound.
            for (let dx = 0;; dx += 1) {
                const gap = axisGap(dx);
                if (gap >= best)
                    break;
                best = Math.min(best, gap + columnGap(x - dx, y), gap + columnGap(x + dx, y));
            }
            clearances[index] = Math.sqrt(best);
        }
    }
    return { grid, clearances };
};
/** The room to stand at a position, or `0` in rock and off the map. Fractional positions count as their cell. */
export const clearanceAt = (field, position) => {
    const index = cellIndex(field.grid, position);
    return index < 0 ? 0 : (field.clearances[index] ?? 0);
};
/**
 * `true` when a disc of this radius may stand centred on this cell.
 *
 * The `> 0` half is what keeps a zero-radius mover honest: rock has a clearance
 * of exactly `0`, and without it `0 >= 0` would declare a point free to stand
 * inside a wall.
 */
export const canOccupy = (field, position, radius) => {
    const clearance = clearanceAt(field, position);
    return clearance > 0 && clearance >= radius;
};
/**
 * `isWalkable` for a disc: walkable ground, wide enough for this mover.
 *
 * An absent mover is the point mover, and for it this is `isWalkable` itself —
 * which is why every existing caller keeps its exact meaning.
 */
export const isPassable = (grid, position, mover) => isWalkable(grid, position) &&
    (mover === undefined || canOccupy(mover.clearance, position, mover.radius));
/**
 * `walkableNeighbours` for a disc: the movement rule, then the erosion.
 *
 * Built by filtering the point rule rather than by re-deriving it, for two
 * reasons. The step set and its order live in exactly one place, `./grid.ts` —
 * and that order is a contract, because A* and Dijkstra break ties by insertion
 * order, so a reshuffle here would silently change which of two equal-cost
 * routes a replay reproduces. And a diagonal is re-checked against *this
 * mover's* flanking cells, not merely against walkable ones: a flank that is
 * floor but only half a cell wide is a flank this mover cannot use, and
 * accepting it would let a disc cut a corner that no sequence of orthogonal
 * steps could reproduce — the exact way the 8-way/4-way equivalence breaks.
 */
export const passableNeighbours = (grid, cell, mover) => {
    if (!isPassable(grid, cell, mover))
        return [];
    if (mover === undefined)
        return walkableNeighbours(grid, cell);
    const x = Math.floor(cell.x);
    const y = Math.floor(cell.y);
    return walkableNeighbours(grid, cell).filter((neighbour) => {
        if (!isPassable(grid, neighbour, mover))
            return false;
        const isDiagonal = neighbour.x !== x && neighbour.y !== y;
        return (!isDiagonal ||
            (isPassable(grid, vec2(neighbour.x, y), mover) && isPassable(grid, vec2(x, neighbour.y), mover)));
    });
};
/**
 * Throws when a mover cannot be applied to this map.
 *
 * A field built for a different grid, or a radius that is negative or NaN, is
 * programmer error rather than an ordinary outcome: answering `null` for it
 * would hide the bug behind an entity that merely refuses to walk. An
 * unreachable destination is the returned result; a nonsense mover is not.
 */
export const assertMover = (grid, mover) => {
    if (mover === undefined)
        return;
    if (mover.clearance.grid !== grid) {
        throw new RangeError('Mover clearance was built for a different map than the one being searched');
    }
    // Phrased as a negated comparison so NaN is rejected too.
    if (!(mover.radius >= 0)) {
        throw new RangeError(`Mover radius must be a non-negative number of cells, received ${mover.radius}`);
    }
};
//# sourceMappingURL=clearance.js.map